<template>
  <b-container class="billing-history">
    <div>
      <h2>Your invoices</h2>
    </div>
    <div>
      <b-table
        borderless
        responsive
        class="history-table"
        :fields="fields"
        :items="listOfInvoices"
        :tbody-tr-class="historyTable"
        show-empty
      >
        <template #empty>
          <h4>You don't have any invoices yet.</h4>
          <h5>
            If you just made a payment, please wait for 24h for your payment to
            be processed.
          </h5>
        </template>
        <template #cell(invoiceNumber)="data">
          <p class="invoiceNumber">
            <InvoiceIcon /> {{ data.item.invoiceNumber }}/{{ data.item.year }}
          </p>
        </template>
        <template #cell(total)="data">
          <p class="total">
            {{ data.item.total }}
          </p>
        </template>
        <template #cell(status)="data">
          <p>
            {{ data.item.status }}
          </p>
        </template>
        <template #cell(dueDate)="data">
          <p class="dueDate">
            {{ formatDateTime(data.item.dueDate) }}
          </p>
        </template>
        <template #cell(download)="data">
          <b-link
            href="#"
            @click="downloadInvoice(data.item)"
            class="download"
            v-if="data.item.status == 'PAID'"
            >Download</b-link
          >
        </template>
      </b-table>
    </div>
  </b-container>
</template>

<script>
import { DateTime } from "luxon";
import { BACKEND_URL } from "../../../config";
import InvoiceIcon from "../../icons/InvoiceIcon.vue";
import { saveAs } from "file-saver";

export default {
  components: {
    InvoiceIcon,
  },
  data() {
    return {
      showDownloadSpinner: false,
      fields: ["invoiceNumber", "total", "status", "dueDate", "download"],
      listOfInvoices: [],
    };
  },
  mounted() {
    this.fetchInvoices();
  },
  methods: {
    formatDateTime(dateTime) {
      return DateTime.fromISO(dateTime).toLocaleString(DateTime.DATE_MED);
    },
    downloadInvoice(invoiceData) {
      console.log("Downloading invoice " + JSON.stringify(invoiceData));

      var customerInfo = sessionStorage.getItem("customerInfo");
      customerInfo = JSON.parse(customerInfo);

      let url = `${BACKEND_URL.Base}/customers/${customerInfo.user.id}/invoices/${invoiceData.year}-${invoiceData.invoiceNumber}?email=${customerInfo.user.email}`;

      fetch(url, {
        method: "get",
        mode: "cors",
        headers: {
          Accept: "application/pdf",
          Authorization: customerInfo.token.token,
        },
        cache: "no-cache",
      })
        .then((response) => {
          if (response.ok) {
            return response.blob();
          } else {
            throw Error(
              "Failed to fetch invoice, http status " + response.status
            );
          }
        })
        .then((blob) => {
          saveAs(
            blob,
            `dropsmart-invoice-${invoiceData.year}-${invoiceData.invoiceNumber}.pdf`
          );
        })
        .catch((error) => {
          console.error(error.message);
        })
        .finally(() => {
          this.showDownloadSpinner = false;
        });
    },
    fetchInvoices() {
      let v = this;
      var customerInfo = sessionStorage.getItem("customerInfo");
      if (!customerInfo) {
        console.error(
          "Customer is not logged in, fetching invoices not allowed."
        );
        return;
      }

      customerInfo = JSON.parse(customerInfo);

      let customerId = customerInfo.user.id;
      let url = `${BACKEND_URL.Base}/customers/${customerId}/invoices`;

      fetch(url, {
        method: "get",
        mode: "cors",
        headers: {
          Accept: "application/json",
          Authorization: customerInfo.token.token,
        },
        cache: "no-cache",
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          } else {
            throw new Error("failed to fetch invoices");
          }
        })
        .then((invoiceData) => {
          let invoices = invoiceData.map((invoice) => {
            return {
              year: invoice.year,
              invoiceNumber: invoice.sequenceNumber,
              dueDate: invoice.invoiceDate,
              total: `${invoice.currencySymbol}${(
                invoice.amountCents / 100.0
              ).toFixed(2)}`,
              status: invoice.status,
              download: `${BACKEND_URL.Base}/customers/${customerId}/invoices/${invoice.year}-${invoice.sequenceNumber}?email=${customerInfo.user.email}`,
            };
          });
          invoices.sort((i1, i2) => {
            let d1 = DateTime.fromISO(i1.dueDate);
            let d2 = DateTime.fromISO(i2.dueDate);
            if (d1 < d2) {
              return 1;
            } else if (d2 < d1) {
              return -1;
            } else {
              return 0;
            }
          });
          v.listOfInvoices = invoices;
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {});
    },
    historyTable(item, type) {
      if (type !== "row") return "";
      if (item.status.toLowerCase() === "paid") return "row-history paid";
      if (item.status.toLowerCase() === "failed") return "row-history failed";
      if (item.status.toLowerCase() === "open") return "row-history open";
      if (item.status.toLowerCase() === "refunded")
        return "row-history refunded";
      if (item.status.toLowerCase() === "processing")
        return "row-history processing";
    },
  },
};
</script>

<style lang="scss">
$color: #000000;

.box {
  color: $color;
  a {
    color: #509610;
    &:hover {
      color: $color;
    }
  }
}

.billing-history {
  max-width: 935px;
  margin: initial;
  padding: 50px 0;
  padding-left: 7%;
  height: 100%;
  h2 {
    font-size: 30px;
    font-weight: 700;
    margin-bottom: 35px;
  }
}

.history-table {
  font-size: 18px;
  background: #ffffff;
  color: #000000;
  thead {
    display: none;
    border-top: 1px solid #b3b3b3;
    border-bottom: 1px solid #b3b3b3;
    font-size: 18px;
    padding: 0 25px;
    th {
      line-height: 30px;
      padding: 1.6875rem 1.5625rem;
      width: 20%;
      &:nth-child(4),
      &:nth-child(5) {
        text-align: right;
      }
    }
  }
  tbody {
    border-top: 1px solid #b3b3b3;
    .row-history {
      .status {
        border-radius: 3px;
        color: #ffffff;
        display: inline-block;
        line-height: 25px;
        padding: 0 7px;
        vertical-align: middle;
      }
    }
    tr {
      border-bottom: 1px solid #b3b3b3;
    }
    td {
      line-height: 30px;
      padding: 1.6875rem 1.5625rem;
      width: 20%;
      &:nth-child(4),
      &:nth-child(5) {
        text-align: right;
      }
    }
    p {
      margin: 0;
      &.invoiceNumber {
        display: flex;
        svg {
          fill: #535353;
          margin-right: 15px;
          width: 15px;
        }
      }
      &.total {
        font-size: 16px;
      }
      &.status {
        font-size: 14px;
      }
      &.dueDate {
        font-size: 16px;
      }
    }
    .download {
      color: #ee4586;
      font-size: 16px;
    }
  }
  .row-history {
    &.paid .status {
      background-color: #49c200;
    }
    &.failed .status {
      background-color: #e90000;
    }
    &.open .status {
      background-color: #ffa200;
    }
    &.refunded .status {
      background-color: #9d009f;
    }
    &.processing .status {
      background-color: #ffd900;
    }
  }
}
@media screen and (max-width: 775px) {
  .billing-history {
    margin-left: 0;
  }
}
</style>
